import React, { useEffect } from 'react'

import { Box } from '@mui/material'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'

import EmptyState from '../../shared/components/EmptyState/EmptyState.component'
import OrderCard from './components/OrderCard/OrderCard.component'
import RecipientCard from './components/RecipientCard/RecipientCard.component'
import RecipientTrackingSkeleton from './components/RecipientTrackingSkeleton/RecipientTrackingSkeleton.component'
import SenderCard from './components/SenderCard/SenderCard.component'
import TrackingHero from './components/TrackingHero/TrackingHero.component'

import { verifyRecipientToken } from '../../redux/states/recipient/verifyRecipientToken/verifyRecipientToken.slice'
import { RootState } from '../../redux/store/store.types'
import routes from '../../router/routes.dictionary'
import { useAppDispatch } from '../../shared/hooks/redux.hooks'
import styles from './RecipientTracking.styles'

const RecipientTracking: React.FC = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { loading, success, error } = useSelector((state: RootState) => state.verifyRecipientToken)

  useEffect(() => {
    if (token == null || token === '') {
      navigate(routes.home.path, { replace: true })

      return
    }
    dispatch(verifyRecipientToken({ token }))
  }, [token, navigate, dispatch])

  if (token == null || token === '') {
    return null
  }

  if (loading) {
    return <RecipientTrackingSkeleton />
  }

  if (error || !success?.data) {
    return (
      <Box sx={styles.pageWrapper}>
        <Box sx={styles.contentArea}>
          <EmptyState
            title="Unable to load tracking"
            subtitle="This link may be invalid or expired. Please use the link from your order email, or contact the sender for a new link."
            button={{
              text: 'Go to home',
              onClick: () => navigate(routes.home.path, { replace: true })
            }}
          />
        </Box>
      </Box>
    )
  }

  const data = success.data

  return (
    <Box sx={styles.pageWrapper}>
      <TrackingHero
        orderId={data.orderId}
        estimatedDeliveryAt={data.trackingDetails?.estimatedDeliveryAt ?? null}
        steps={data.trackingDetails?.steps ?? []}
        supportEmail={data.supportEmail ?? null}
      />

      <Box sx={styles.contentArea}>
        <Box sx={styles.leftColumn}>
          <RecipientCard recipient={data.recipient} />
          <SenderCard sender={data.sender} />
        </Box>

        <Box sx={styles.rightColumn}>
          <OrderCard orderId={data.orderId} items={data.items} package={data.package} />
        </Box>
      </Box>
    </Box>
  )
}

export default RecipientTracking
